import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Search,
  ShoppingCart,
  RotateCw,
  X,
  ExternalLink,
  DollarSign,
  Trash2,
  Edit3,
  Check,
  Filter,
  Package,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isAdmin, canAccessDepartment } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";

const STATUS_FILTERS = [
  { label: "Active Requests", value: "Active" },
  { label: "Pending", value: "Pending" },
  { label: "Partially Priced", value: "Partially Priced" },
  { label: "In Progress", value: "In Progress" },
  { label: "Price Received", value: "Price Received" },
  { label: "Completed", value: "Completed" },
  { label: "All Requests", value: "All" },
];

const PRIORITY_OPTIONS = ["All", "Urgent", "High", "Medium", "Low"];

function MetricBox({ label, value, tone = "blue", onClick, active, testId }) {
  return (
    <div
      className={`metric ${tone}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        transition: "all 0.15s ease",
        padding: "16px 18px",
        ...(active ? { outline: "2px solid var(--navy)", outlineOffset: "-1px", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" } : {}),
      }}
      title={onClick ? `Filter by ${label}` : undefined}
      data-testid={testId || `metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="metric-top" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</span>
        {active && <span style={{ fontSize: 9.5, fontWeight: 800, color: "var(--navy)", background: "#e2e8f0", padding: "1px 5px", borderRadius: 3 }}>FILTERED</span>}
      </div>
      <strong style={{ fontSize: 24, margin: "8px 0 0", fontWeight: 800, display: "block", color: "var(--navy)" }}>{value}</strong>
    </div>
  );
}

function formatCurrency(amount, currency = "INR") {
  if (amount === null || amount === undefined || isNaN(amount)) return "—";
  const symbol = currency === "INR" ? "₹" : currency === "USD" ? "$" : currency === "EUR" ? "€" : `${currency} `;
  return `${symbol}${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

export default function Procurement({ user, onOpenLead, onOpenClient, onNavigate, onOpenRequest }) {
  const isSuperOrAdmin = isAdmin(user);

  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({
    total_active: 0,
    total: 0,
    pending: 0,
    partially_priced: 0,
    in_progress: 0,
    price_received: 0,
    completed: 0,
    urgent: 0,
  });
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters - Default to "Active" so completed requests are hidden by default
  const [search, setSearch] = useSavedState("procurement", "search", "");
  const [selectedStatus, setSelectedStatus] = useSavedState("procurement", "selectedStatus", "Active");
  const [selectedPriority, setSelectedPriority] = useSavedState("procurement", "selectedPriority", "All");
  const [selectedAssignee, setSelectedAssignee] = useSavedState("procurement", "selectedAssignee", "");
  const [selectedRequiredBy, setSelectedRequiredBy] = useSavedState("procurement", "selectedRequiredBy", "");

  // Delete Modal State
  const [deletingReq, setDeletingReq] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Load Procurement team users for filter dropdown
  useEffect(() => {
    api("get", "/users?department=Procurement&active_only=true")
      .then((res) => {
        if (Array.isArray(res.data)) {
          const list = res.data.filter((u) => {
            if (u.is_active === false || u.is_deleted === true) return false;
            const depts = Array.isArray(u.departments) && u.departments.length > 0
              ? u.departments.map((d) => (d || "").toLowerCase().trim())
              : [(u.department || "").toLowerCase().trim()];
            const role = (u.role || "").toLowerCase().trim();
            return depts.includes("procurement") || role === "procurement";
          });
          list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setUsersList(list);
        }
      })
      .catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append("search", search.trim());
      if (selectedStatus) params.append("status", selectedStatus);
      if (selectedPriority !== "All") params.append("priority", selectedPriority);
      if (selectedAssignee) params.append("assigned_to_id", selectedAssignee);
      if (selectedRequiredBy) params.append("required_by", selectedRequiredBy);

      const [reqsRes, sumRes] = await Promise.all([
        api("get", `/procurement?${params.toString()}`),
        api("get", "/procurement/summary"),
      ]);

      setRequests(Array.isArray(reqsRes.data) ? reqsRes.data : []);
      if (sumRes.data) setSummary(sumRes.data);
    } catch (err) {
      toast.error("Failed to load procurement requests");
    } finally {
      setLoading(false);
    }
  }, [search, selectedStatus, selectedPriority, selectedAssignee, selectedRequiredBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    paginatedItems: paginatedRequests,
    totalItems: totalFilteredRequests,
  } = usePagination(requests, 10, { sectionKey: "procurement" });

  const handleDelete = async () => {
    if (!deletingReq) return;
    setDeleteBusy(true);
    try {
      await api("delete", `/procurement/${deletingReq.request_id}`);
      toast.success(`Request ${deletingReq.request_id} removed`);
      setDeletingReq(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete request");
    } finally {
      setDeleteBusy(false);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setSelectedStatus("Active");
    setSelectedPriority("All");
    setSelectedAssignee("");
    setSelectedRequiredBy("");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    search ||
    selectedStatus !== "Active" ||
    selectedPriority !== "All" ||
    selectedAssignee ||
    selectedRequiredBy;

  return (
    <div className="content" style={{ maxWidth: "100%", paddingBottom: 40 }} data-testid="procurement-page">
      {/* Page Heading matching Leads */}
      <div className="page-heading">
        <div>
          <div className="small-label">PROCUREMENT</div>
          <h1>Procurement</h1>
          <p className="muted">
            Manage pricing requests for Sales leads and sync verified rates.
          </p>
        </div>
        <div className="stack" style={{ flexDirection: "row", gap: 10, alignItems: "center" }}>
          <button
            type="button"
            className="outline-btn compact"
            onClick={loadData}
            title="Refresh list"
            data-testid="refresh-procurement-btn"
          >
            <RotateCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary / Metric Cards (Clickable Interactive Filters matching Leads) */}
      <div
        className="metrics-grid"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 12,
          marginBottom: 18,
        }}
        data-testid="procurement-summary-strip"
      >
        <MetricBox
          label="Active Requests"
          value={summary.total_active || 0}
          tone="blue"
          active={selectedStatus === "Active" && selectedPriority === "All"}
          onClick={() => {
            setSelectedStatus("Active");
            setSelectedPriority("All");
            setCurrentPage(1);
          }}
          testId="procurement-metric-active"
        />
        <MetricBox
          label="Pending"
          value={summary.pending || 0}
          tone="amber"
          active={selectedStatus === "Pending" && selectedPriority === "All"}
          onClick={() => {
            setSelectedStatus(selectedStatus === "Pending" ? "Active" : "Pending");
            setSelectedPriority("All");
            setCurrentPage(1);
          }}
          testId="procurement-metric-pending"
        />
        <MetricBox
          label="Partially Priced"
          value={summary.partially_priced || 0}
          tone="amber"
          active={selectedStatus === "Partially Priced" && selectedPriority === "All"}
          onClick={() => {
            setSelectedStatus(selectedStatus === "Partially Priced" ? "Active" : "Partially Priced");
            setSelectedPriority("All");
            setCurrentPage(1);
          }}
          testId="procurement-metric-partially-priced"
        />
        <MetricBox
          label="In Progress"
          value={summary.in_progress || 0}
          tone="blue"
          active={selectedStatus === "In Progress" && selectedPriority === "All"}
          onClick={() => {
            setSelectedStatus(selectedStatus === "In Progress" ? "Active" : "In Progress");
            setSelectedPriority("All");
            setCurrentPage(1);
          }}
          testId="procurement-metric-in-progress"
        />
        <MetricBox
          label="Price Received"
          value={summary.price_received || 0}
          tone="green"
          active={selectedStatus === "Price Received" && selectedPriority === "All"}
          onClick={() => {
            setSelectedStatus(selectedStatus === "Price Received" ? "Active" : "Price Received");
            setSelectedPriority("All");
            setCurrentPage(1);
          }}
          testId="procurement-metric-price-received"
        />
        <MetricBox
          label="Urgent"
          value={summary.urgent || 0}
          tone="red"
          active={selectedPriority === "Urgent"}
          onClick={() => {
            setSelectedPriority((prev) => (prev === "Urgent" ? "All" : "Urgent"));
            setCurrentPage(1);
          }}
          testId="procurement-metric-urgent"
        />
        <MetricBox
          label="Completed History"
          value={summary.completed || 0}
          tone="blue"
          active={selectedStatus === "Completed"}
          onClick={() => {
            setSelectedStatus(selectedStatus === "Completed" ? "Active" : "Completed");
            setSelectedPriority("All");
            setCurrentPage(1);
          }}
          testId="procurement-metric-completed"
        />
      </div>

      {/* Search and Filters Toolbar matching Leads */}
      <div className="toolbar" data-testid="procurement-filter-bar">
        {/* Search */}
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search product, client, request ID…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            data-testid="procurement-search-input"
          />
          {search && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
              data-testid="procurement-search-clear"
              style={{ padding: 2, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <select
          value={selectedStatus}
          onChange={(e) => {
            setSelectedStatus(e.target.value);
            setCurrentPage(1);
          }}
          data-testid="procurement-status-filter"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        {/* Priority Filter */}
        <select
          value={selectedPriority}
          onChange={(e) => {
            setSelectedPriority(e.target.value);
            setCurrentPage(1);
          }}
          data-testid="procurement-priority-filter"
        >
          <option value="All">All Priorities</option>
          {PRIORITY_OPTIONS.filter((p) => p !== "All").map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        {/* Assignee Filter */}
        <select
          value={selectedAssignee}
          onChange={(e) => {
            setSelectedAssignee(e.target.value);
            setCurrentPage(1);
          }}
          data-testid="procurement-assignee-filter"
        >
          <option value="">All Specialists</option>
          {usersList.map((u) => {
            const extra = [u.email, u.company].filter(Boolean).join(" · ");
            return (
              <option key={u.user_id} value={u.user_id}>
                {u.name}{extra ? ` (${extra})` : ""}
              </option>
            );
          })}
        </select>

        {/* Required-by Date Filter */}
        <input
          type="date"
          value={selectedRequiredBy}
          onChange={(e) => {
            setSelectedRequiredBy(e.target.value);
            setCurrentPage(1);
          }}
          title="Filter by required-by date"
          data-testid="procurement-date-filter"
          style={{
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: 12,
            color: "#1e293b",
            height: 38,
          }}
        />

        {hasActiveFilters && (
          <button
            type="button"
            className="outline-btn compact"
            onClick={resetFilters}
            data-testid="procurement-reset-filters-btn"
            style={{ fontSize: 12 }}
            title="Reset all filters"
          >
            Reset Filters
          </button>
        )}
      </div>

      {/* Requests Table Panel matching Leads */}
      <section className="panel" style={{ padding: 0, overflow: "hidden" }}>
        <div className="panel-title" style={{ padding: "16px 20px 14px", margin: 0 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>
              {selectedStatus === "Completed" ? "Completed Requests" : "Procurement Requests"}{" "}
              <span className="count">{requests.length}</span>
            </h3>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
            <RotateCw size={18} className="spin" style={{ marginBottom: 8 }} />
            <div>Loading procurement requests…</div>
          </div>
        ) : requests.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center" }}>
            <ShoppingCart size={28} style={{ color: "var(--muted)", margin: "0 auto 10px", opacity: 0.4 }} />
            <h3 style={{ margin: "0 0 4px", fontSize: 15, color: "var(--navy)", fontWeight: 600 }}>
              {selectedStatus === "Completed" ? "No completed requests found" : "No price requests found"}
            </h3>
            <p style={{ margin: 0, fontSize: 12.5, color: "var(--muted)" }}>
              {hasActiveFilters
                ? "No price requests match the selected filters."
                : selectedStatus === "Completed"
                ? "Requests marked as Completed will appear here."
                : "New price requests created from Sales leads will appear here."}
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="crm-table" data-testid="procurement-table">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Request ID</th>
                  <th style={{ minWidth: 160 }}>Lead / Client</th>
                  <th style={{ minWidth: 180 }}>Products</th>
                  <th style={{ width: 100 }}>Quantity</th>
                  <th style={{ width: 90 }}>Priority</th>
                  <th style={{ width: 110 }}>Required By</th>
                  <th style={{ width: 130 }}>Assigned To</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th style={{ width: 120, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRequests.map((r) => {
                  const itemsList = r.items || [];
                  const prodCount = itemsList.length || r.items_count || 1;

                  // Format quantities: e.g. "20 + 50" for multi-product or "20 units" for single
                  const quantityDisplay =
                    r.quantities_summary ||
                    (itemsList.length > 1
                      ? itemsList.map((it) => (it.quantity ? (Number(it.quantity) % 1 === 0 ? Number(it.quantity) : it.quantity) : 1)).join(" + ")
                      : `${(r.quantity || 1).toLocaleString()} ${r.unit || "units"}`);

                  return (
                    <tr
                      key={r.request_id}
                      style={{ cursor: "pointer" }}
                      onClick={() => onOpenRequest && onOpenRequest(r.request_id)}
                      data-testid={`procurement-row-${r.request_id}`}
                    >
                      {/* Request ID */}
                      <td style={{ fontWeight: 700, color: "var(--navy)" }} className="mono">
                        {r.request_id}
                      </td>

                      {/* Lead / Client */}
                      <td>
                        <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13 }}>
                          {r.company_name || "—"}
                        </div>
                        <div style={{ fontSize: 11, marginTop: 2 }}>
                          {r.client_id ? (
                            onOpenClient ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenClient(r.client_id);
                                }}
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  color: "#0284c7",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 2,
                                  fontWeight: 500,
                                }}
                                title="View Client Details"
                                data-testid={`open-client-${r.client_id}`}
                              >
                                {r.client_id} <ExternalLink size={10} />
                              </button>
                            ) : (
                              <span style={{ color: "var(--muted)" }}>{r.client_id}</span>
                            )
                          ) : r.lead_id ? (
                            onOpenLead ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenLead(r.lead_id);
                                }}
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  color: "#0284c7",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 2,
                                  fontWeight: 500,
                                }}
                                title="View Lead Details"
                                data-testid={`open-lead-${r.lead_id}`}
                              >
                                {r.lead_id} <ExternalLink size={10} />
                              </button>
                            ) : (
                              <span style={{ color: "var(--muted)" }}>{r.lead_id}</span>
                            )
                          ) : (
                            <span style={{ color: "var(--muted)" }}>—</span>
                          )}
                        </div>
                      </td>

                      {/* Products & Count */}
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {prodCount > 1 && (
                            <span
                              className="badge blue"
                              style={{
                                fontSize: 10.5,
                                padding: "2px 6px",
                                whiteSpace: "nowrap",
                              }}
                              data-testid={`product-count-badge-${r.request_id}`}
                            >
                              {prodCount} Products
                            </span>
                          )}
                          <span
                            style={{
                              fontWeight: 500,
                              color: "var(--navy)",
                              fontSize: 12.5,
                            }}
                            title={r.product_name}
                          >
                            {r.product_name}
                          </span>
                        </div>
                      </td>

                      {/* Quantity */}
                      <td style={{ color: "var(--navy)", fontWeight: 600, fontSize: 12.5, whiteSpace: "nowrap" }}>
                        {quantityDisplay}
                      </td>

                      {/* Priority */}
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span
                          className={`badge ${
                            r.priority === "Urgent"
                              ? "red"
                              : r.priority === "High"
                              ? "amber"
                              : "gray"
                          }`}
                          style={{ fontSize: 10.5, padding: "2px 7px" }}
                        >
                          {r.priority || "Normal"}
                        </span>
                      </td>

                      {/* Required By */}
                      <td style={{ color: "var(--navy)", whiteSpace: "nowrap", fontSize: 12.5 }}>
                        {formatDate(r.required_by)}
                      </td>

                      {/* Assigned To */}
                      <td style={{ color: "#334155", whiteSpace: "nowrap", fontSize: 12.5 }}>
                        {r.assigned_to_name || "—"}
                      </td>

                      {/* Status */}
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span
                          className={`badge ${
                            r.status === "Price Received"
                              ? "green"
                              : r.status === "In Progress"
                              ? "blue"
                              : r.status === "Partially Priced"
                              ? "purple"
                              : r.status === "Completed"
                              ? "gray"
                              : "amber"
                          }`}
                          style={{ fontSize: 11, padding: "3px 8px" }}
                        >
                          {r.status}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <div style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="outline-btn compact"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenRequest && onOpenRequest(r.request_id);
                            }}
                            data-testid={`view-request-btn-${r.request_id}`}
                            style={{ fontSize: 11.5, height: 28, padding: "0 10px", fontWeight: 600 }}
                          >
                            View Request
                          </button>

                          {isSuperOrAdmin && (
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletingReq(r);
                              }}
                              title="Delete"
                              data-testid={`delete-req-btn-${r.request_id}`}
                              style={{ height: 28, width: 28, padding: 0, borderRadius: 6, color: "var(--muted)" }}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && requests.length > 0 && (
          <div style={{ padding: "8px 16px" }}>
            <Pagination
              currentPage={currentPage}
              totalItems={totalFilteredRequests}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              itemLabel="price requests"
            />
          </div>
        )}
      </section>

      {/* Delete Confirmation Modal */}
      {deletingReq && (
        <div className="modal-backdrop" style={{ zIndex: 10000 }}>
          <div
            className="modal"
            style={{ maxWidth: 440, padding: 22 }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#fee2e2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AlertTriangle size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: "0 0 4px 0", fontSize: 17, color: "var(--navy)" }}>Delete Price Request</h2>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--navy)", lineHeight: 1.4 }}>
                  Are you sure you want to delete request #{deletingReq.request_id}?
                </p>
              </div>
              <button type="button" className="icon-btn" onClick={() => setDeletingReq(null)}>
                <X size={16} />
              </button>
            </div>

            <div
              style={{
                background: "#f8fafc",
                border: "1px solid var(--line)",
                borderRadius: 6,
                padding: "10px 14px",
                marginBottom: 20,
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              <div style={{ color: "var(--navy)", fontWeight: 600 }}>{deletingReq.product_name}</div>
              <div style={{ color: "#64748b" }}>{deletingReq.company_name}</div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setDeletingReq(null)}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleDelete}
                disabled={deleteBusy}
                style={{ background: "var(--red)", borderColor: "var(--red)" }}
                data-testid="confirm-delete-req-btn"
              >
                {deleteBusy ? "Deleting…" : "Delete Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
